import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2, Clock, Download, FileCheck, FileText, Hourglass, MessageSquare, Paperclip, Send, Upload, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { money, relTime, STATUS_LABEL, STATUS_ORDER } from "@/lib/format";
import type { Asset, JobCheckpoint, JobDetailBundle, JobMessage } from "@/lib/types";
import { StatusBadge } from "@/components/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

function initials(n?: string | null, h?: string | null) {
  return (n || h || "?").trim().split(/\s+/).slice(0, 2).map(s => s[0]).join("").toUpperCase();
}

function parseJson<T>(s: string | null | undefined, fallback: T): T {
  if (!s) return fallback;
  try { return JSON.parse(s) as T; } catch { return fallback; }
}

function AssetLink({ asset }: { asset: Asset }) {
  const isImage = asset.mime?.startsWith("image/");
  return (
    <a
      href={`/api/assets/${asset.id}`}
      target="_blank"
      rel="noreferrer"
      className="group flex items-center gap-2 rounded-md border bg-muted/30 p-2 text-sm hover:bg-accent"
    >
      {isImage ? (
        <img src={`/api/assets/${asset.id}`} alt={asset.filename} className="h-10 w-10 rounded object-cover" />
      ) : (
        <FileText className="h-5 w-5 text-muted-foreground" />
      )}
      <span className="flex-1 truncate">{asset.filename}</span>
      <Download className="h-4 w-4 text-muted-foreground transition group-hover:text-foreground" />
    </a>
  );
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<JobDetailBundle | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [messageText, setMessageText] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  async function reload() {
    try {
      const res = await api.get<JobDetailBundle>(`/api/jobs/${id}`);
      setData(res);
    } catch {
      setNotFound(true);
    }
  }

  useEffect(() => { reload(); }, [id]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages.length]);

  if (notFound) return <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">Job not found.</div>;
  if (!data) {
    return (
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <Skeleton className="h-[60vh]" /><Skeleton className="h-64" />
      </div>
    );
  }

  const { job, messages, checkpoints, payments, assets, creator, client } = data;
  const isCreator = user?.id === creator.id;
  const isClient = user?.id === client.id;
  const brief = parseJson<Record<string, unknown>>(job.brief_json, {});
  const addons = parseJson<{ id: string; label: string; priceCents: number }[]>(job.addons_json, []);

  async function postMessage(e: FormEvent) {
    e.preventDefault();
    if (!messageText.trim()) return;
    try {
      await api.post(`/api/jobs/${id}/messages`, { body: messageText });
      setMessageText("");
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  async function moveStatus(status: string) {
    try {
      await api.patch(`/api/jobs/${id}/status`, { status });
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  async function markPaid(paymentId: string) {
    try {
      await api.post(`/api/jobs/${id}/payments/mark-paid`, { paymentId });
      reload();
      toast.success("Marked paid");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  async function submitQuote(quotedPriceDollars: string, depositDollars: string, message: string) {
    const quotedPriceCents = Math.round(Number(quotedPriceDollars) * 100);
    const depositCents = depositDollars ? Math.round(Number(depositDollars) * 100) : undefined;
    try {
      await api.post(`/api/jobs/${id}/quote`, { quotedPriceCents, depositCents, message: message || null });
      reload();
      toast.success("Quote sent");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  async function uploadCheckpointAsset(file: File) {
    const up = await api.upload(file, { kind: "wip", jobId: id });
    return up.id;
  }

  async function submitCheckpoint(label: string, kind: "sketch" | "wip" | "final", file: File | null, note: string) {
    try {
      let assetId: string | null = null;
      if (file) assetId = await uploadCheckpointAsset(file);
      await api.post(`/api/jobs/${id}/checkpoints`, { label, kind, assetId, note: note || null });
      reload();
      toast.success("Checkpoint posted");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  async function respondCheckpoint(cp: JobCheckpoint, action: "approve" | "revise", note: string) {
    try {
      await api.post(`/api/jobs/${id}/checkpoints/${cp.id}/respond`, { action, note: note || null });
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  async function requestRevision(note: string, kind: "minor" | "major") {
    try {
      const res = await api.post<{ beyondIncluded: boolean }>(`/api/jobs/${id}/revisions`, { note, kind });
      reload();
      if (res.beyondIncluded) toast.info("Beyond included revisions — creator may apply a surcharge.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  async function deliverFile(file: File) {
    try {
      await api.upload(file, { kind: "delivery", jobId: id });
      reload();
      toast.success("Delivery uploaded");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "failed");
    }
  }

  const currentStageIdx = STATUS_ORDER.indexOf(job.status as any);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <div className="space-y-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {isCreator ? "Request from" : "Request to"}{" "}
            <Link to={`/${isCreator ? client.handle : creator.handle}`} className="underline underline-offset-4">
              @{isCreator ? client.handle : creator.handle}
            </Link>
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">{job.service_title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StatusBadge status={job.status} />
            {job.queue_position && (
              <Badge variant="outline" className="gap-1">
                <Clock className="h-3 w-3" /> Queue #{job.queue_position}
              </Badge>
            )}
            <Badge variant="outline" className="font-normal">
              {job.revisions_used}/{job.revisions_allowed} revisions used
            </Badge>
          </div>
        </div>

        {currentStageIdx >= 0 && (
          <div className="relative overflow-x-auto rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2">
              {STATUS_ORDER.map((s, i) => {
                const passed = i <= currentStageIdx;
                return (
                  <div key={s} className="flex flex-1 items-center gap-2">
                    <div className={[
                      "h-2 flex-1 rounded-full",
                      passed ? "bg-foreground" : "bg-muted",
                    ].join(" ")} />
                    <span className={[
                      "whitespace-nowrap text-[11px] uppercase tracking-wider",
                      i === currentStageIdx ? "font-semibold" : "text-muted-foreground",
                    ].join(" ")}>{STATUS_LABEL[s]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isCreator && job.status === "pending_quote" && (
          <QuoteForm currency={job.currency} onSubmit={submitQuote} />
        )}

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Brief</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {Object.keys(brief).length === 0 ? (
              <p className="text-muted-foreground">No brief fields.</p>
            ) : (
              Object.entries(brief).map(([k, v]) => (
                <div key={k}>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {k === "__note" ? "Note" : k}
                  </p>
                  <div className="mt-0.5">
                    {Array.isArray(v)
                      ? (
                        <ul className="space-y-1">
                          {(v as any[]).map((item, i) => (
                            <li key={i}>
                              {typeof item === "object" && item && "url" in item ? (
                                <a href={(item as any).url} target="_blank" className="underline underline-offset-4">
                                  {(item as any).filename ?? (item as any).url}
                                </a>
                              ) : String(item)}
                            </li>
                          ))}
                        </ul>
                      )
                      : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}
                  </div>
                </div>
              ))
            )}
            {addons.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Add-ons</p>
                  <ul className="mt-1 space-y-0.5">
                    {addons.map(a => (
                      <li key={a.id} className="flex justify-between">
                        <span>{a.label}</span>
                        <span className="tabular-nums text-muted-foreground">
                          +{money(a.priceCents, job.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Checkpoints</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {checkpoints.length === 0 && (
              <p className="text-sm text-muted-foreground">No checkpoints yet.</p>
            )}
            {checkpoints.map(cp => (
              <CheckpointRow
                key={cp.id}
                cp={cp}
                asset={assets.find(a => a.id === cp.asset_id) ?? null}
                canRespond={isClient && cp.status === "pending"}
                onRespond={(action, note) => respondCheckpoint(cp, action, note)}
              />
            ))}
            {isCreator && (
              <NewCheckpointForm onSubmit={submitCheckpoint} />
            )}
          </CardContent>
        </Card>

        {isClient && (job.status === "awaiting_feedback" || job.status === "in_progress") && (
          <RevisionForm revisionsUsed={job.revisions_used} revisionsAllowed={job.revisions_allowed} onSubmit={requestRevision} />
        )}

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base inline-flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Conversation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {messages.map(m => <MessageRow key={m.id} m={m} isSelf={m.author_id === user?.id} />)}
              <div ref={messagesEndRef} />
            </div>
            <form onSubmit={postMessage} className="mt-4 flex items-start gap-2">
              <Textarea
                rows={2}
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                placeholder="Reply to this job…"
                onKeyDown={e => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    postMessage(e as unknown as FormEvent);
                  }
                }}
              />
              <Button type="submit" size="icon" disabled={!messageText.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
            <p className="mt-1 text-[11px] text-muted-foreground">⌘ + Enter to send</p>
          </CardContent>
        </Card>

        {assets.some(a => a.kind === "delivery") && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base inline-flex items-center gap-2"><FileCheck className="h-4 w-4" /> Delivery vault</CardTitle></CardHeader>
            <CardContent className="grid gap-2 sm:grid-cols-2">
              {assets.filter(a => a.kind === "delivery").map(a => <AssetLink key={a.id} asset={a} />)}
            </CardContent>
          </Card>
        )}

        {isCreator && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Upload delivery</CardTitle></CardHeader>
            <CardContent>
              <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed p-4 text-sm hover:bg-accent">
                <Upload className="h-4 w-4" />
                <span>Attach final files to the delivery vault</span>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  onChange={async e => {
                    const files = Array.from(e.target.files ?? []);
                    for (const f of files) await deliverFile(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </CardContent>
          </Card>
        )}
      </div>

      <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Parties</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={creator.avatar_url ?? undefined} />
                <AvatarFallback>{initials(creator.display_name, creator.handle)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-xs text-muted-foreground">Creator</p>
                <Link to={`/${creator.handle}`} className="font-medium underline underline-offset-4">
                  {creator.display_name ?? `@${creator.handle}`}
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={client.avatar_url ?? undefined} />
                <AvatarFallback>{initials(client.display_name, client.handle)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-xs text-muted-foreground">Client</p>
                <p className="font-medium">{client.display_name ?? `@${client.handle}`}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Payments</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {payments.length === 0 && <p className="text-muted-foreground">No payments scheduled yet.</p>}
            {payments.map(p => (
              <div key={p.id} className="flex items-start justify-between gap-2 rounded-md border p-2">
                <div>
                  <p className="font-medium capitalize">{p.kind}</p>
                  <p className="tabular-nums">{money(p.amount_cents, p.currency)}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.status === "paid"
                      ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3 w-3" /> paid {relTime(p.paid_at)}</span>
                      : <span className="inline-flex items-center gap-1"><Hourglass className="h-3 w-3" /> pending</span>}
                  </p>
                </div>
                {isClient && p.status === "pending" && creator.payment_link && (
                  <Button asChild variant="outline" size="sm">
                    <a href={creator.payment_link} target="_blank" rel="noreferrer">Pay</a>
                  </Button>
                )}
                {isCreator && p.status === "pending" && (
                  <Button size="sm" variant="outline" onClick={() => markPaid(p.id)}>
                    Mark paid
                  </Button>
                )}
              </div>
            ))}
            {isClient && !creator.payment_link && (
              <p className="text-xs text-muted-foreground">
                The creator hasn't linked a payment method yet — they may send instructions in-thread.
              </p>
            )}
          </CardContent>
        </Card>

        {isCreator && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Move status</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {(["queued", "in_progress", "awaiting_feedback", "delivered", "complete"] as const).map(s => (
                  <Button key={s} size="sm" variant="outline" onClick={() => moveStatus(s)}>{STATUS_LABEL[s]}</Button>
                ))}
              </div>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => moveStatus("rejected")}>
                <XCircle className="mr-1 h-4 w-4" /> Reject
              </Button>
            </CardContent>
          </Card>
        )}

        {isClient && job.status === "complete" && (
          <TestimonialForm jobId={job.id} onDone={reload} />
        )}

        {isClient && (["submitted", "pending_quote", "awaiting_deposit", "queued"] as string[]).includes(job.status) && (
          <Button variant="outline" className="w-full text-destructive" onClick={() => moveStatus("cancelled")}>
            Cancel request
          </Button>
        )}
      </aside>
    </div>
  );
}

function MessageRow({ m, isSelf }: { m: JobMessage; isSelf: boolean }) {
  const system = m.kind !== "comment";
  if (system) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Paperclip className="h-3 w-3" />
        <span>{m.body}</span>
        <span className="opacity-60">· {relTime(m.created_at)}</span>
      </div>
    );
  }
  return (
    <div className={["flex gap-2", isSelf ? "justify-end" : ""].join(" ")}>
      {!isSelf && (
        <Avatar className="h-7 w-7">
          <AvatarImage src={m.avatar_url ?? undefined} />
          <AvatarFallback className="text-xs">{initials(m.display_name, m.handle)}</AvatarFallback>
        </Avatar>
      )}
      <div className={[
        "max-w-[70%] rounded-lg px-3 py-2 text-sm",
        isSelf ? "bg-foreground text-background" : "bg-muted",
      ].join(" ")}>
        <p className="whitespace-pre-wrap">{m.body}</p>
        <p className={["mt-1 text-[10px]", isSelf ? "text-background/60" : "text-muted-foreground"].join(" ")}>{relTime(m.created_at)}</p>
      </div>
    </div>
  );
}

function CheckpointRow({ cp, asset, canRespond, onRespond }: {
  cp: JobCheckpoint;
  asset: Asset | null;
  canRespond: boolean;
  onRespond: (action: "approve" | "revise", note: string) => void;
}) {
  const [note, setNote] = useState("");
  const toneMap = {
    pending: "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200",
    approved: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
    revision_requested: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
  } as const;
  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">{cp.label}</p>
          <p className="text-xs text-muted-foreground capitalize">{cp.kind}</p>
        </div>
        <span className={["rounded-full px-2 py-0.5 text-[11px]", toneMap[cp.status]].join(" ")}>
          {cp.status.replace(/_/g, " ")}
        </span>
      </div>
      {cp.note && <p className="mt-2 text-sm text-muted-foreground">{cp.note}</p>}
      {asset && <div className="mt-3"><AssetLink asset={asset} /></div>}
      {canRespond && (
        <div className="mt-3 flex flex-col gap-2 md:flex-row">
          <Input placeholder="Optional note…" value={note} onChange={e => setNote(e.target.value)} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onRespond("approve", note)}>Approve</Button>
            <Button size="sm" variant="outline" onClick={() => onRespond("revise", note)}>Request revision</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function NewCheckpointForm({ onSubmit }: { onSubmit: (label: string, kind: "sketch" | "wip" | "final", file: File | null, note: string) => Promise<void> }) {
  const [label, setLabel] = useState("Sketch for approval");
  const [kind, setKind] = useState<"sketch" | "wip" | "final">("sketch");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      onSubmit={async e => {
        e.preventDefault();
        setBusy(true);
        await onSubmit(label, kind, file, note);
        setBusy(false);
        setNote(""); setFile(null);
      }}
      className="space-y-2 rounded-md border border-dashed p-3"
    >
      <p className="text-sm font-medium">Post a checkpoint</p>
      <div className="grid gap-2 md:grid-cols-2">
        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label" />
        <Select value={kind} onValueChange={v => setKind(v as typeof kind)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sketch">Sketch</SelectItem>
            <SelectItem value="wip">WIP</SelectItem>
            <SelectItem value="final">Final</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Notes for the client (optional)…" />
      <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} className="text-xs" />
      <Button type="submit" size="sm" disabled={busy}>{busy ? "Posting…" : "Post checkpoint"}</Button>
    </form>
  );
}

function QuoteForm({ currency, onSubmit }: { currency: string; onSubmit: (price: string, deposit: string, message: string) => Promise<void> }) {
  const [price, setPrice] = useState("");
  const [deposit, setDeposit] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form
      onSubmit={async e => {
        e.preventDefault();
        setBusy(true);
        await onSubmit(price, deposit, msg);
        setBusy(false);
      }}
      className="rounded-lg border bg-card p-4"
    >
      <div className="mb-2 flex items-center gap-2">
        <Hourglass className="h-4 w-4" />
        <p className="font-medium">Quote this request</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label className="text-xs">Total price ({currency})</Label>
          <Input value={price} onChange={e => setPrice(e.target.value)} required inputMode="decimal" />
        </div>
        <div>
          <Label className="text-xs">Deposit ({currency}) — defaults to 50%</Label>
          <Input value={deposit} onChange={e => setDeposit(e.target.value)} inputMode="decimal" />
        </div>
      </div>
      <Textarea rows={2} className="mt-2" placeholder="Message for the client…" value={msg} onChange={e => setMsg(e.target.value)} />
      <Button type="submit" className="mt-3" disabled={busy}>{busy ? "Sending…" : "Send quote"}</Button>
    </form>
  );
}

function RevisionForm({ revisionsUsed, revisionsAllowed, onSubmit }: {
  revisionsUsed: number; revisionsAllowed: number;
  onSubmit: (note: string, kind: "minor" | "major") => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const [kind, setKind] = useState<"minor" | "major">("minor");
  const beyond = kind === "minor" && revisionsUsed >= revisionsAllowed;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Request a revision</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <Select value={kind} onValueChange={v => setKind(v as typeof kind)}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="minor">Minor — within scope</SelectItem>
            <SelectItem value="major">Major composition change</SelectItem>
          </SelectContent>
        </Select>
        <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="What should change?" />
        {beyond && (
          <Alert>
            <AlertTitle className="text-sm">Beyond included rounds</AlertTitle>
            <AlertDescription className="text-xs">
              You've used {revisionsUsed} of {revisionsAllowed} included revisions. The creator may apply a surcharge.
            </AlertDescription>
          </Alert>
        )}
        <Button
          size="sm"
          onClick={async () => {
            if (!note.trim()) return;
            await onSubmit(note, kind);
            setNote("");
          }}
        >
          Send revision request
        </Button>
      </CardContent>
    </Card>
  );
}

function TestimonialForm({ jobId, onDone }: { jobId: string; onDone: () => void }) {
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Leave kudos</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              className={n <= rating ? "text-amber-500" : "text-muted-foreground"}
            >
              ★
            </button>
          ))}
        </div>
        <Textarea rows={3} value={body} onChange={e => setBody(e.target.value)} placeholder="How did it go? Public testimonial." />
        <Button
          size="sm"
          onClick={async () => {
            try {
              await api.post(`/api/jobs/${jobId}/testimonial`, { rating, body: body || null });
              toast.success("Thanks — posted!");
              onDone();
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "failed");
            }
          }}
        >
          Post
        </Button>
      </CardContent>
    </Card>
  );
}
